import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { WidgetsContext } from './../core/context';
import { ParallaxProvider } from "react-scroll-parallax";

import styles from './landingPage.module.scss';
import get from 'lodash/get';
import size from 'lodash/size';
import filter from 'lodash/filter';
import sumBy from 'lodash/sumBy';


export function LandingPage({ children, onChange, pageWidgets }) {
  const widgetContext = React.useContext(WidgetsContext);
  const breakpoint = get(widgetContext, 'breakpoint');
  const selector = widgetContext.selector;

  const pageRef = React.useRef(null);
  const iframeBodyRef = React.useRef(null);

  const [state, setState] = useState({
    pageHeight: 'auto'
  })

  const pageWidget = useSelector((state) => {
    const widgets = get(state, `${selector}.widgets`) || [];
    const firstPageUid = get(pageWidgets, '[0].uid');
    if (!firstPageUid) {
      return null;
    }
    return widgets.find(item => item.uid == firstPageUid);
  });

  const pageHeight = useSelector((state) => {
    const widgets = get(state, `${selector}.widgets`, []);
  
    return sumBy(
      filter(
        widgets,
        (w) =>
          w.type === "cleverstory.v2.widgets.page" &&
          get(w, `properties.${breakpoint}.visible`, true) !== false
      ),
      (w) => {
        const h = get(w, `properties.${breakpoint}.layout.height`, 0);
        return typeof h === "string" ? parseInt(h, 10) || 0 : h;
      }
    );
  });

  const container = get(pageWidget, `properties.${breakpoint}.container`);

  React.useEffect(() => {
    var isInIframe = window.parent !== window;
    if (isInIframe) {
      const parentWindow = window.parent;
      parentWindow.postMessage({
        type: "cleverstory:embed:page:loading",
        payload: {
          pageWidget: pageWidget
        }
      }, '*'); 
    }
  },[]);

  React.useEffect(() => {
    if (pageWidgets && size(pageWidgets) > 0) {
      const windowWidth = document.body.clientWidth;
      var zoomValue = 1;
      if (widgetContext.shouldZoom) {
        zoomValue = windowWidth / container.minWidth;

        if (windowWidth > 1200) {
          zoomValue = 1;
        }
      }

      const heightReduction = breakpoint === 'desktop' ? filter(pageWidgets, (w) => get(w, 'properties.desktop.visible', true) !== false).length : 0;
      
      setState((state) => {
        return {
          ...state,
          pageHeight: `${(parseInt(pageHeight) * zoomValue) - heightReduction}px`
        }
      })
    }
  }, [pageWidgets, pageHeight, breakpoint]);

  React.useEffect(() => {
    const handleEmbedScroll = (event) => {
      const { type, payload } = event.data;

      if (type === "cleverstory:embed:scroll"){
        const { percentage } = payload || {};
        try {
          onChange('progress', {
            index: 0,
            progress: percentage
          });
        }
        catch {}
      }
    };

    window.addEventListener("message", handleEmbedScroll);

    return () => {
      window.removeEventListener("message", handleEmbedScroll);
    };
  }, []);

  useEffect(() => {
    if (widgetContext.preview) {
      const parentDocument = window.parent.document;
      const iframe = parentDocument.getElementById("cs-preview-iframe");
      const iframeDocument = iframe?.contentDocument || iframe?.contentWindow?.document;
      iframeBodyRef.current = iframeDocument?.body;
    }
  }, [widgetContext.preview]);

  useEffect(() => {
    if (pageRef && parseInt(state.pageHeight) > 0) {
      var isInIframe = window.parent !== window;
      // Run once on mount
      if (!isInIframe){
        calculateScrollPosition();
      }

      document.addEventListener('scroll', calculateScrollPosition);

      const clientHeight = document.body.clientHeight;
      if (clientHeight > parseInt(state.pageHeight)) {
        onChange('progress', {
          index: 0,
          progress: 100
        });
      }

      return () => {
        if (process.browser) {
          document.removeEventListener('scroll', calculateScrollPosition);
        }
      }
    }
  }, [state.pageHeight, pageRef]);

  const calculateScrollPosition = () => {
    const scrollTop = window.scrollY;
    const clientHeight = document.body.clientHeight;
    const scrollHeight = pageRef.current.scrollHeight;

    var percent = (scrollTop + clientHeight) * 100 / scrollHeight;
    percent = Math.max(0, percent);
    percent = Math.min(100, percent);

    onChange('progress', {
      index: 0,
      progress: percent
    });
  }

  var pageStyles = {
    height: state.pageHeight,
  }

  return (
    <div className={styles.landingPage} style={pageStyles} ref={pageRef}>
      <ParallaxProvider scrollContainer={iframeBodyRef.current}>
        {children}
      </ParallaxProvider>
    </div>
  )
}
